<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { onMounted, ref, type Ref } from "vue";
import type {
  AntiAutomationProvider,
  AuditIdentityPurgeResult,
  AuditIdentityRetentionMode,
  AuditIdentityRetentionSettings,
  EmailDeliveryCategory,
  EmailDeliveryRoute,
  EmailTemplateKey,
  NotificationRuntimeSettings,
  ProbeOperationsSettings,
  RegistrationMode,
  RegistrationSettings,
  SaveSmtpProfileInput,
  SendTestEmailResult,
  SmtpAuthentication,
  SmtpProfile,
  SmtpTransportSecurity,
} from "../api/system-settings.js";
import AppShell from "../components/AppShell.vue";
import EmailTemplateSettings from "../components/EmailTemplateSettings.vue";
import { confirmDialog } from "../dialogs/app-dialog.js";
import { formatDateTime } from "../formatting/date-time.js";
import { useSystemSettingsStore } from "../stores/system-settings.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

interface DeliveryCategoryOption {
  readonly value: EmailDeliveryCategory;
  readonly label: string;
  readonly description: string;
}

type SettingsSection = "general" | "probes" | "notifications" | "email";
type RouteSelections = Record<EmailDeliveryCategory, string>;

const CATEGORY_OPTIONS: readonly DeliveryCategoryOption[] = [
  { value: "alerts", label: "Alerts", description: "Probe outages and monitoring alerts" },
  { value: "invitations", label: "Invitations", description: "Tenant and user invitations" },
  {
    value: "account_security",
    label: "Account security",
    description: "Verification, password recovery, and email-based MFA",
  },
  { value: "general", label: "General communication", description: "Other system messages" },
];

const settingsStore: ReturnType<typeof useSystemSettingsStore> = useSystemSettingsStore();
const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const activeSection: Ref<SettingsSection> = ref("general");
const registrationMode: Ref<RegistrationMode> = ref("invite_only");
const antiAutomationProvider: Ref<AntiAutomationProvider | null> = ref(null);
const blockDisposableEmailDomains: Ref<boolean> = ref(true);
const blockUntrustedEmailDomains: Ref<boolean> = ref(true);
const enrollmentCodeTtlSeconds: Ref<string | number> = ref(600);
const enrollmentClaimRateLimitMax: Ref<string | number> = ref(20);
const enrollmentClaimRateLimitWindowSeconds: Ref<string | number> = ref(60);
const uploadDiagnosticsRetentionDays: Ref<string | number> = ref(1);
const uploadWarningWifiRssiDbm: Ref<string | number> = ref(-80);
const uploadWarningConsecutiveFailures: Ref<string | number> = ref(1);
const uploadWarningQueueUsageRatioPercent: Ref<string | number> = ref(80);
const alertEvaluationIntervalSeconds: Ref<string | number> = ref(15);
const alertEvaluationBatchSize: Ref<string | number> = ref(100);
const outboxPollIntervalSeconds: Ref<string | number> = ref(5);
const outboxBatchSize: Ref<string | number> = ref(25);
const outboxLeaseSeconds: Ref<string | number> = ref(60);
const outboxRetryBaseDelaySeconds: Ref<string | number> = ref(60);
const outboxRetryMaxDelaySeconds: Ref<string | number> = ref(3_600);
const outboxMaxAttempts: Ref<string | number> = ref(8);
const auditRetentionMode: Ref<AuditIdentityRetentionMode> = ref("duration");
const auditRetentionDays: Ref<string | number> = ref(365);
const selectedProfileId: Ref<string | null> = ref(null);
const smtpName: Ref<string> = ref("");
const smtpEnabled: Ref<boolean> = ref(false);
const smtpHost: Ref<string> = ref("");
const smtpPort: Ref<string | number> = ref(587);
const smtpTransportSecurity: Ref<SmtpTransportSecurity> = ref("starttls_required");
const smtpAuthentication: Ref<SmtpAuthentication> = ref("password");
const smtpUsername: Ref<string> = ref("");
const smtpPassword: Ref<string> = ref("");
const smtpPasswordConfigured: Ref<boolean> = ref(false);
const smtpClearPassword: Ref<boolean> = ref(false);
const smtpFromEmail: Ref<string> = ref("");
const smtpFromName: Ref<string> = ref("LacunaScope");
const smtpReplyToEmail: Ref<string> = ref("");
const routeSelections: Ref<RouteSelections> = ref({
  alerts: "",
  invitations: "",
  account_security: "",
  general: "",
});
const loadingError: Ref<string | null> = ref(null);
const registrationMessage: Ref<string | null> = ref(null);
const probeOperationsMessage: Ref<string | null> = ref(null);
const notificationRuntimeMessage: Ref<string | null> = ref(null);
const auditRetentionMessage: Ref<string | null> = ref(null);
const smtpMessage: Ref<string | null> = ref(null);
const routingMessage: Ref<string | null> = ref(null);
const registrationSaveFailed: Ref<boolean> = ref(false);
const probeOperationsSaveFailed: Ref<boolean> = ref(false);
const notificationRuntimeSaveFailed: Ref<boolean> = ref(false);
const auditRetentionSaveFailed: Ref<boolean> = ref(false);
const smtpSaveFailed: Ref<boolean> = ref(false);
const routingSaveFailed: Ref<boolean> = ref(false);
const savingRegistration: Ref<boolean> = ref(false);
const savingProbeOperations: Ref<boolean> = ref(false);
const savingNotificationRuntime: Ref<boolean> = ref(false);
const savingAuditRetention: Ref<boolean> = ref(false);
const purgingAuditIdentities: Ref<boolean> = ref(false);
const savingSmtp: Ref<boolean> = ref(false);
const savingRouting: Ref<boolean> = ref(false);
const testEmailOpen: Ref<boolean> = ref(false);
const testEmailRecipient: Ref<string> = ref("");
const testEmailTemplateKey: Ref<EmailTemplateKey> = ref("general_notification");
const sendingTestEmail: Ref<boolean> = ref(false);
const testEmailMessage: Ref<string | null> = ref(null);
const testEmailFailed: Ref<boolean> = ref(false);

function errorText(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "The system settings request failed.";
}

function applyRegistrationSettings(settings: RegistrationSettings): void {
  registrationMode.value = settings.mode;
  antiAutomationProvider.value = settings.antiAutomationProvider;
  blockDisposableEmailDomains.value = settings.blockDisposableEmailDomains;
  blockUntrustedEmailDomains.value = settings.blockUntrustedEmailDomains;
}

function applyProbeOperationsSettings(settings: ProbeOperationsSettings): void {
  enrollmentCodeTtlSeconds.value = settings.enrollmentCodeTtlSeconds;
  enrollmentClaimRateLimitMax.value = settings.enrollmentClaimRateLimitMax;
  enrollmentClaimRateLimitWindowSeconds.value = settings.enrollmentClaimRateLimitWindowSeconds;
  uploadDiagnosticsRetentionDays.value = settings.uploadDiagnosticsRetentionDays;
  uploadWarningWifiRssiDbm.value = settings.uploadWarningWifiRssiDbm;
  uploadWarningConsecutiveFailures.value = settings.uploadWarningConsecutiveFailures;
  uploadWarningQueueUsageRatioPercent.value = Math.round(settings.uploadWarningQueueUsageRatio * 100);
}

function applyNotificationRuntimeSettings(settings: NotificationRuntimeSettings): void {
  alertEvaluationIntervalSeconds.value = settings.alertEvaluationIntervalMs / 1000;
  alertEvaluationBatchSize.value = settings.alertEvaluationBatchSize;
  outboxPollIntervalSeconds.value = settings.outboxPollIntervalMs / 1000;
  outboxBatchSize.value = settings.outboxBatchSize;
  outboxLeaseSeconds.value = settings.outboxLeaseMs / 1000;
  outboxRetryBaseDelaySeconds.value = settings.outboxRetryBaseDelayMs / 1000;
  outboxRetryMaxDelaySeconds.value = settings.outboxRetryMaxDelayMs / 1000;
  outboxMaxAttempts.value = settings.outboxMaxAttempts;
}

function applyAuditIdentityRetention(settings: AuditIdentityRetentionSettings): void {
  auditRetentionMode.value = settings.mode;
  auditRetentionDays.value = settings.retentionDays ?? 365;
}

function resetProfileForm(): void {
  selectedProfileId.value = null;
  smtpName.value = "";
  smtpEnabled.value = false;
  smtpHost.value = "";
  smtpPort.value = 587;
  smtpTransportSecurity.value = "starttls_required";
  smtpAuthentication.value = "password";
  smtpUsername.value = "";
  smtpPassword.value = "";
  smtpPasswordConfigured.value = false;
  smtpClearPassword.value = false;
  smtpFromEmail.value = "";
  smtpFromName.value = "LacunaScope";
  smtpReplyToEmail.value = "";
  smtpMessage.value = null;
  testEmailOpen.value = false;
  testEmailMessage.value = null;
}

function applySmtpProfile(profile: SmtpProfile): void {
  selectedProfileId.value = profile.id;
  smtpName.value = profile.name;
  smtpEnabled.value = profile.enabled;
  smtpHost.value = profile.host ?? "";
  smtpPort.value = profile.port ?? "";
  smtpTransportSecurity.value = profile.transportSecurity;
  smtpAuthentication.value = profile.authentication;
  smtpUsername.value = profile.username ?? "";
  smtpPassword.value = "";
  smtpPasswordConfigured.value = profile.passwordConfigured;
  smtpClearPassword.value = false;
  smtpFromEmail.value = profile.fromEmail ?? "";
  smtpFromName.value = profile.fromName ?? "";
  smtpReplyToEmail.value = profile.replyToEmail ?? "";
  smtpMessage.value = null;
  testEmailOpen.value = false;
  testEmailMessage.value = null;
}

function applyRoutes(routes: readonly EmailDeliveryRoute[]): void {
  const selections: RouteSelections = {
    alerts: "",
    invitations: "",
    account_security: "",
    general: "",
  };
  for (const route of routes) {
    selections[route.category] = route.smtpProfileId ?? "";
  }
  routeSelections.value = selections;
}

async function loadSettings(): Promise<void> {
  loadingError.value = null;
  try {
    await settingsStore.load();
    if (settingsStore.registration !== null) {
      applyRegistrationSettings(settingsStore.registration);
    }
    if (settingsStore.probeOperations !== null) {
      applyProbeOperationsSettings(settingsStore.probeOperations);
    }
    if (settingsStore.notificationRuntime !== null) {
      applyNotificationRuntimeSettings(settingsStore.notificationRuntime);
    }
    if (settingsStore.auditIdentityRetention !== null) {
      applyAuditIdentityRetention(settingsStore.auditIdentityRetention);
    }
    const profiles: readonly SmtpProfile[] = settingsStore.smtpProfiles?.profiles ?? [];
    if (profiles.length > 0 && profiles[0] !== undefined) {
      applySmtpProfile(profiles[0]);
    } else {
      resetProfileForm();
    }
    applyRoutes(settingsStore.emailRouting?.routes ?? []);
  } catch (error: unknown) {
    loadingError.value = errorText(error);
  }
}

async function saveProbeOperations(): Promise<void> {
  savingProbeOperations.value = true;
  probeOperationsMessage.value = null;
  probeOperationsSaveFailed.value = false;
  try {
    const settings: ProbeOperationsSettings = await settingsStore.updateProbeOperations({
      enrollmentCodeTtlSeconds: Number(enrollmentCodeTtlSeconds.value),
      enrollmentClaimRateLimitMax: Number(enrollmentClaimRateLimitMax.value),
      enrollmentClaimRateLimitWindowSeconds: Number(enrollmentClaimRateLimitWindowSeconds.value),
      uploadDiagnosticsRetentionDays: Number(uploadDiagnosticsRetentionDays.value),
      uploadWarningWifiRssiDbm: Number(uploadWarningWifiRssiDbm.value),
      uploadWarningConsecutiveFailures: Number(uploadWarningConsecutiveFailures.value),
      uploadWarningQueueUsageRatio: Number(uploadWarningQueueUsageRatioPercent.value) / 100,
    });
    applyProbeOperationsSettings(settings);
    probeOperationsMessage.value = "Probe operations policy saved.";
  } catch (error: unknown) {
    probeOperationsSaveFailed.value = true;
    probeOperationsMessage.value = errorText(error);
  } finally {
    savingProbeOperations.value = false;
  }
}

async function saveNotificationRuntime(): Promise<void> {
  savingNotificationRuntime.value = true;
  notificationRuntimeMessage.value = null;
  notificationRuntimeSaveFailed.value = false;
  try {
    const settings: NotificationRuntimeSettings = await settingsStore.updateNotificationRuntime({
      alertEvaluationIntervalMs: Number(alertEvaluationIntervalSeconds.value) * 1000,
      alertEvaluationBatchSize: Number(alertEvaluationBatchSize.value),
      outboxPollIntervalMs: Number(outboxPollIntervalSeconds.value) * 1000,
      outboxBatchSize: Number(outboxBatchSize.value),
      outboxLeaseMs: Number(outboxLeaseSeconds.value) * 1000,
      outboxRetryBaseDelayMs: Number(outboxRetryBaseDelaySeconds.value) * 1000,
      outboxRetryMaxDelayMs: Number(outboxRetryMaxDelaySeconds.value) * 1000,
      outboxMaxAttempts: Number(outboxMaxAttempts.value),
    });
    applyNotificationRuntimeSettings(settings);
    notificationRuntimeMessage.value = "Notification runtime settings saved for the next worker run.";
  } catch (error: unknown) {
    notificationRuntimeSaveFailed.value = true;
    notificationRuntimeMessage.value = errorText(error);
  } finally {
    savingNotificationRuntime.value = false;
  }
}

async function saveRegistration(): Promise<void> {
  savingRegistration.value = true;
  registrationMessage.value = null;
  registrationSaveFailed.value = false;
  try {
    const settings: RegistrationSettings = await settingsStore.updateRegistration({
      mode: registrationMode.value,
      antiAutomationProvider: antiAutomationProvider.value,
      blockDisposableEmailDomains: blockDisposableEmailDomains.value,
      blockUntrustedEmailDomains: blockUntrustedEmailDomains.value,
    });
    applyRegistrationSettings(settings);
    registrationMessage.value = "Registration policy saved.";
  } catch (error: unknown) {
    registrationSaveFailed.value = true;
    registrationMessage.value = errorText(error);
  } finally {
    savingRegistration.value = false;
  }
}

async function saveAuditRetention(): Promise<void> {
  savingAuditRetention.value = true;
  auditRetentionMessage.value = null;
  auditRetentionSaveFailed.value = false;
  try {
    const retentionDays: number | null = auditRetentionMode.value === "duration"
      ? Number(auditRetentionDays.value)
      : null;
    const settings: AuditIdentityRetentionSettings =
      await settingsStore.updateAuditIdentityRetention({
        mode: auditRetentionMode.value,
        retentionDays,
      });
    applyAuditIdentityRetention(settings);
    auditRetentionMessage.value = "Audit identity retention saved for future audit events.";
  } catch (error: unknown) {
    auditRetentionSaveFailed.value = true;
    auditRetentionMessage.value = errorText(error);
  } finally {
    savingAuditRetention.value = false;
  }
}

async function purgeAuditIdentities(): Promise<void> {
  const confirmed: boolean = await confirmDialog({ title: "Purge expired audit identities?", message: "Identity snapshots whose individually assigned retention period has expired will be removed. Audit events themselves remain unchanged.", confirmLabel: "Purge identities", tone: "danger" });
  if (!confirmed) return;
  purgingAuditIdentities.value = true;
  auditRetentionMessage.value = null;
  auditRetentionSaveFailed.value = false;
  try {
    const result: AuditIdentityPurgeResult = await settingsStore.purgeExpiredAuditIdentities();
    auditRetentionMessage.value = `${result.purgedIdentities} expired audit identit${result.purgedIdentities === 1 ? "y" : "ies"} purged.`;
  } catch (error: unknown) {
    auditRetentionSaveFailed.value = true;
    auditRetentionMessage.value = errorText(error);
  } finally {
    purgingAuditIdentities.value = false;
  }
}

function smtpInput(): SaveSmtpProfileInput {
  const normalizedPort: string = String(smtpPort.value).trim();
  const baseInput: SaveSmtpProfileInput = {
    name: smtpName.value,
    enabled: smtpEnabled.value,
    host: smtpHost.value.trim() === "" ? null : smtpHost.value,
    port: normalizedPort === "" ? null : Number(normalizedPort),
    transportSecurity: smtpTransportSecurity.value,
    authentication: smtpAuthentication.value,
    username: smtpUsername.value.trim() === "" ? null : smtpUsername.value,
    fromEmail: smtpFromEmail.value.trim() === "" ? null : smtpFromEmail.value,
    fromName: smtpFromName.value.trim() === "" ? null : smtpFromName.value,
    replyToEmail: smtpReplyToEmail.value.trim() === "" ? null : smtpReplyToEmail.value,
  };
  if (smtpPassword.value !== "") {
    return { ...baseInput, password: smtpPassword.value };
  }
  return smtpClearPassword.value ? { ...baseInput, clearPassword: true } : baseInput;
}

async function saveSmtp(): Promise<void> {
  savingSmtp.value = true;
  smtpMessage.value = null;
  smtpSaveFailed.value = false;
  try {
    const profileId: string | null = selectedProfileId.value;
    const creating: boolean = profileId === null;
    let profile: SmtpProfile;
    if (profileId === null) {
      profile = await settingsStore.createSmtp(smtpInput());
    } else {
      profile = await settingsStore.updateSmtp(profileId, smtpInput());
    }
    applySmtpProfile(profile);
    applyRoutes(settingsStore.emailRouting?.routes ?? []);
    smtpMessage.value = creating ? "SMTP profile created." : "SMTP profile saved.";
  } catch (error: unknown) {
    smtpSaveFailed.value = true;
    smtpMessage.value = errorText(error);
  } finally {
    savingSmtp.value = false;
  }
}

async function removeSelectedProfile(): Promise<void> {
  const profileId: string | null = selectedProfileId.value;
  if (profileId === null) return;
  const confirmed: boolean = await confirmDialog({ title: "Delete SMTP profile?", message: "Assigned profiles cannot be deleted. This operation permanently removes an unassigned profile.", confirmLabel: "Delete profile", tone: "danger" });
  if (!confirmed) return;
  savingSmtp.value = true;
  smtpSaveFailed.value = false;
  try {
    await settingsStore.deleteSmtp(profileId);
    const firstProfile: SmtpProfile | undefined = settingsStore.smtpProfiles?.profiles[0];
    if (firstProfile === undefined) {
      resetProfileForm();
    } else {
      applySmtpProfile(firstProfile);
    }
    smtpMessage.value = "SMTP profile deleted.";
  } catch (error: unknown) {
    smtpSaveFailed.value = true;
    smtpMessage.value = errorText(error);
  } finally {
    savingSmtp.value = false;
  }
}

async function saveRouting(): Promise<void> {
  savingRouting.value = true;
  routingMessage.value = null;
  routingSaveFailed.value = false;
  try {
    for (const category of CATEGORY_OPTIONS) {
      const selectedId: string = routeSelections.value[category.value];
      await settingsStore.updateEmailRoute(category.value, selectedId === "" ? null : selectedId);
    }
    applyRoutes(settingsStore.emailRouting?.routes ?? []);
    routingMessage.value = "Message routing saved.";
  } catch (error: unknown) {
    routingSaveFailed.value = true;
    routingMessage.value = errorText(error);
  } finally {
    savingRouting.value = false;
  }
}

async function sendTestEmail(): Promise<void> {
  const profileId: string | null = selectedProfileId.value;
  if (profileId === null) {
    return;
  }
  sendingTestEmail.value = true;
  testEmailMessage.value = null;
  testEmailFailed.value = false;
  try {
    const result: SendTestEmailResult = await settingsStore.sendTestEmail(profileId, {
      recipientEmail: testEmailRecipient.value,
      templateKey: testEmailTemplateKey.value,
    });
    const acceptedAt: string = formatDateTime(result.acceptedAt, userSettingsStore.settings.dateTimeFormat);
    testEmailMessage.value = result.auditRecorded
      ? `The SMTP server accepted the test message at ${acceptedAt}. This does not guarantee inbox delivery.`
      : `The SMTP server accepted the test message at ${acceptedAt}, but the audit event could not be recorded.`;
  } catch (error: unknown) {
    testEmailFailed.value = true;
    testEmailMessage.value = errorText(error);
  } finally {
    sendingTestEmail.value = false;
  }
}

onMounted(loadSettings);
</script>

<template>
  <AppShell>
    <header class="page-heading">
      <div>
        <p class="eyebrow">System administration</p>
        <h1>Settings</h1>
        <p>Registration, audit identity retention, abuse protection, and outbound email delivery.</p>
      </div>
    </header>

    <p v-if="loadingError" class="form-error settings-load-error" role="alert">{{ loadingError }}</p>

    <div v-if="settingsStore.status === 'ready'" class="settings-grid">
      <nav class="page-tabs" aria-label="Settings sections">
        <button class="page-tab" :class="{ 'router-link-active': activeSection === 'general' }" type="button" @click="activeSection = 'general'">General</button>
        <button class="page-tab" :class="{ 'router-link-active': activeSection === 'probes' }" type="button" @click="activeSection = 'probes'">Probes</button>
        <button class="page-tab" :class="{ 'router-link-active': activeSection === 'notifications' }" type="button" @click="activeSection = 'notifications'">Notifications</button>
        <button class="page-tab" :class="{ 'router-link-active': activeSection === 'email' }" type="button" @click="activeSection = 'email'">Email</button>
      </nav>

      <section v-if="activeSection === 'general'" class="content-panel settings-card">
        <div class="settings-card__heading">
          <div><p class="eyebrow">Identity</p><h2>Registration</h2></div>
          <span class="status-badge" :class="registrationMode === 'invite_only' ? 'status-badge--active' : 'status-badge--suspended'">
            {{ registrationMode === "invite_only" ? "Invite only" : "Open signup" }}
          </span>
        </div>
        <form class="settings-form" @submit.prevent="saveRegistration">
          <label><span>Registration mode</span><select v-model="registrationMode"><option value="invite_only">Join by invitation only</option><option value="open_verified_email">Open signup with verified email</option></select></label>
          <label><span>Anti-automation provider</span><select v-model="antiAutomationProvider"><option :value="null">Not configured</option><option value="altcha">ALTCHA · self-hosted</option><option value="turnstile">Cloudflare Turnstile</option></select></label>
          <label class="settings-checkbox"><input v-model="blockDisposableEmailDomains" type="checkbox" /><span>Block disposable email domains</span></label>
          <label class="settings-checkbox"><input v-model="blockUntrustedEmailDomains" type="checkbox" /><span>Block untrusted email services</span></label>
          <div v-if="registrationMode === 'open_verified_email'" class="notice notice--warning">Open signup remains fail-closed until email verification, domain lists, and the selected challenge provider are operational.</div>
          <p v-if="registrationMessage" :class="registrationSaveFailed ? 'form-error' : 'settings-message'" :role="registrationSaveFailed ? 'alert' : 'status'">{{ registrationMessage }}</p>
          <button class="button button--primary" type="submit" :disabled="savingRegistration">{{ savingRegistration ? "Saving…" : "Save registration policy" }}</button>
        </form>
      </section>

      <section v-if="activeSection === 'general'" class="content-panel settings-card">
        <div class="settings-card__heading">
          <div><p class="eyebrow">Audit</p><h2>Identity retention</h2></div>
          <span class="status-badge status-badge--active">Prospective</span>
        </div>
        <form class="settings-form" @submit.prevent="saveAuditRetention">
          <label><span>Retention for new audit identities</span><select v-model="auditRetentionMode"><option value="immediate">Do not retain identity</option><option value="duration">Retain for a duration</option><option value="unlimited">Retain without expiry</option></select></label>
          <label v-if="auditRetentionMode === 'duration'"><span>Retention days</span><input v-model="auditRetentionDays" type="number" min="1" max="365000" required inputmode="numeric" /></label>
          <div class="notice settings-field--wide">Changes apply only to audit events created afterwards. Existing identity expiry dates are never recalculated. Audit-event rotation remains a separate operator responsibility.</div>
          <p v-if="auditRetentionMessage" :class="auditRetentionSaveFailed ? 'form-error' : 'settings-message'" :role="auditRetentionSaveFailed ? 'alert' : 'status'">{{ auditRetentionMessage }}</p>
          <div class="settings-actions settings-field--wide"><span></span><div><button class="button button--quiet" type="button" :disabled="purgingAuditIdentities || savingAuditRetention" @click="purgeAuditIdentities">{{ purgingAuditIdentities ? "Purging…" : "Purge expired identities" }}</button><button class="button button--primary" type="submit" :disabled="savingAuditRetention || purgingAuditIdentities">{{ savingAuditRetention ? "Saving…" : "Save audit retention" }}</button></div></div>
        </form>
      </section>

      <section v-if="activeSection === 'probes'" class="content-panel settings-card">
        <div class="settings-card__heading">
          <div><p class="eyebrow">Probes</p><h2>Operations policy</h2></div>
          <span class="status-badge status-badge--active">Runtime</span>
        </div>
        <form class="settings-form" @submit.prevent="saveProbeOperations">
          <label><span>Enrollment code lifetime seconds</span><input v-model="enrollmentCodeTtlSeconds" type="number" min="60" max="86400" required inputmode="numeric" /></label>
          <label><span>Enrollment claim limit</span><input v-model="enrollmentClaimRateLimitMax" type="number" min="1" max="10000" required inputmode="numeric" /></label>
          <label><span>Claim window seconds</span><input v-model="enrollmentClaimRateLimitWindowSeconds" type="number" min="1" max="86400" required inputmode="numeric" /></label>
          <label><span>Upload diagnostics retention days</span><input v-model="uploadDiagnosticsRetentionDays" type="number" min="1" max="365000" required inputmode="numeric" /></label>
          <label><span>Weak Wi-Fi RSSI below dBm</span><input v-model="uploadWarningWifiRssiDbm" type="number" min="-128" max="0" required inputmode="numeric" /></label>
          <label><span>Upload failures warning at</span><input v-model="uploadWarningConsecutiveFailures" type="number" min="1" max="10000" required inputmode="numeric" /></label>
          <label><span>Queue usage warning percent</span><input v-model="uploadWarningQueueUsageRatioPercent" type="number" min="1" max="100" required inputmode="numeric" /></label>
          <div class="notice settings-field--wide">These values are read at runtime. Enrollment claim limits protect the public deployment-code claim endpoint; upload diagnostics are retained for the configured duration and warning thresholds control probe inventory diagnostics only.</div>
          <p v-if="probeOperationsMessage" :class="probeOperationsSaveFailed ? 'form-error' : 'settings-message'" :role="probeOperationsSaveFailed ? 'alert' : 'status'">{{ probeOperationsMessage }}</p>
          <button class="button button--primary" type="submit" :disabled="savingProbeOperations">{{ savingProbeOperations ? "Saving…" : "Save probe policy" }}</button>
        </form>
      </section>

      <section v-if="activeSection === 'notifications'" class="content-panel settings-card">
        <div class="settings-card__heading">
          <div><p class="eyebrow">Notifications</p><h2>Worker runtime</h2></div>
          <span class="status-badge status-badge--active">Next run</span>
        </div>
        <form class="settings-form" @submit.prevent="saveNotificationRuntime">
          <label><span>Alert evaluation interval seconds</span><input v-model="alertEvaluationIntervalSeconds" type="number" min="1" max="3600" step="1" required inputmode="numeric" /></label>
          <label><span>Alert evaluation batch size</span><input v-model="alertEvaluationBatchSize" type="number" min="1" max="10000" required inputmode="numeric" /></label>
          <label><span>Outbox poll interval seconds</span><input v-model="outboxPollIntervalSeconds" type="number" min="1" max="3600" step="1" required inputmode="numeric" /></label>
          <label><span>Outbox batch size</span><input v-model="outboxBatchSize" type="number" min="1" max="1000" required inputmode="numeric" /></label>
          <label><span>Outbox lease seconds</span><input v-model="outboxLeaseSeconds" type="number" min="1" max="3600" step="1" required inputmode="numeric" /></label>
          <label><span>Retry base delay seconds</span><input v-model="outboxRetryBaseDelaySeconds" type="number" min="1" max="86400" step="1" required inputmode="numeric" /></label>
          <label><span>Retry max delay seconds</span><input v-model="outboxRetryMaxDelaySeconds" type="number" min="1" max="86400" step="1" required inputmode="numeric" /></label>
          <label><span>Max delivery attempts</span><input v-model="outboxMaxAttempts" type="number" min="1" max="100" required inputmode="numeric" /></label>
          <div class="notice settings-field--wide">Alert and outbox workers reload these values before each run. An already scheduled sleep is not interrupted; the following run uses the saved batch, lease, and retry values and schedules the next sleep with the saved interval.</div>
          <p v-if="notificationRuntimeMessage" :class="notificationRuntimeSaveFailed ? 'form-error' : 'settings-message'" :role="notificationRuntimeSaveFailed ? 'alert' : 'status'">{{ notificationRuntimeMessage }}</p>
          <button class="button button--primary" type="submit" :disabled="savingNotificationRuntime">{{ savingNotificationRuntime ? "Saving…" : "Save notification runtime" }}</button>
        </form>
      </section>

      <section v-if="activeSection === 'email'" class="content-panel settings-card">
        <div class="settings-card__heading">
          <div><p class="eyebrow">Email delivery</p><h2>SMTP profiles</h2></div>
          <button class="button button--quiet" type="button" @click="resetProfileForm">Add profile</button>
        </div>
        <div class="smtp-profile-layout">
          <nav class="smtp-profile-list" aria-label="SMTP profiles">
            <button
              v-for="profile in settingsStore.smtpProfiles?.profiles ?? []"
              :key="profile.id"
              type="button"
              :class="{ 'smtp-profile-list__item--selected': selectedProfileId === profile.id }"
              class="smtp-profile-list__item"
              @click="applySmtpProfile(profile)"
            >
              <span><strong>{{ profile.name }}</strong><small>{{ profile.host ?? "Incomplete configuration" }}</small></span>
              <span class="status-dot" :class="profile.enabled && profile.configurationComplete ? 'status-dot--active' : 'status-dot--disabled'"></span>
            </button>
            <p v-if="(settingsStore.smtpProfiles?.profiles.length ?? 0) === 0" class="prepared-empty-state__safety">No SMTP profiles exist yet.</p>
          </nav>

          <form class="settings-form settings-form--smtp" @submit.prevent="saveSmtp">
            <label class="settings-field--wide"><span>Profile name</span><input v-model="smtpName" maxlength="120" required placeholder="Transactional email" /></label>
            <label class="settings-checkbox settings-checkbox--wide"><input v-model="smtpEnabled" type="checkbox" /><span>Enable this SMTP profile</span></label>
            <label><span>Host</span><input v-model="smtpHost" maxlength="320" placeholder="smtp.example.com" /></label>
            <label><span>Port</span><input v-model="smtpPort" type="number" min="1" max="65535" inputmode="numeric" /></label>
            <label><span>Transport security</span><select v-model="smtpTransportSecurity"><option value="starttls_required">STARTTLS required</option><option value="implicit_tls">Implicit TLS</option></select></label>
            <label><span>Authentication</span><select v-model="smtpAuthentication"><option value="password">Username and password</option><option value="none">No authentication</option></select></label>
            <label v-if="smtpAuthentication === 'password'"><span>Username</span><input v-model="smtpUsername" maxlength="320" autocomplete="username" /></label>
            <label v-if="smtpAuthentication === 'password'"><span>New password</span><input v-model="smtpPassword" type="password" maxlength="4096" autocomplete="new-password" :disabled="settingsStore.smtpProfiles?.credentialEncryptionAvailable !== true" :placeholder="selectedProfileId === null ? 'SMTP password' : 'Leave blank to keep current password'" /></label>
            <label v-if="smtpAuthentication === 'password' && smtpPasswordConfigured" class="settings-checkbox"><input v-model="smtpClearPassword" type="checkbox" :disabled="smtpPassword !== ''" /><span>Remove stored password</span></label>
            <label><span>From email</span><input v-model="smtpFromEmail" type="email" maxlength="320" placeholder="monitor@example.com" /></label>
            <label><span>From name</span><input v-model="smtpFromName" maxlength="120" /></label>
            <label class="settings-field--wide"><span>Reply-to email · optional</span><input v-model="smtpReplyToEmail" type="email" maxlength="320" /></label>
            <div v-if="settingsStore.smtpProfiles?.credentialEncryptionAvailable !== true" class="notice notice--warning settings-field--wide">Set <code>SETTINGS_ENCRYPTION_KEY</code> before storing SMTP credentials.</div>
            <div class="settings-actions settings-field--wide">
              <p v-if="smtpMessage" :class="smtpSaveFailed ? 'form-error' : 'settings-message'" :role="smtpSaveFailed ? 'alert' : 'status'">{{ smtpMessage }}</p>
              <div>
                <button v-if="selectedProfileId !== null" class="button button--danger" type="button" :disabled="savingSmtp" @click="removeSelectedProfile">Delete</button>
                <button class="button button--quiet" type="button" :disabled="savingSmtp" @click="testEmailOpen = !testEmailOpen">Send test email</button>
                <button class="button button--primary" type="submit" :disabled="savingSmtp">{{ savingSmtp ? "Saving…" : selectedProfileId === null ? "Create profile" : "Save profile" }}</button>
              </div>
            </div>
            <div v-if="testEmailOpen && selectedProfileId !== null" class="smtp-test-panel settings-field--wide">
              <div>
                <label><span>Test recipient</span><input v-model="testEmailRecipient" type="email" maxlength="320" required placeholder="you@example.com" /></label>
                <label><span>Template with example data</span><select v-model="testEmailTemplateKey">
                  <option v-for="template in settingsStore.emailTemplates?.templates ?? []" :key="template.key" :value="template.key">{{ template.displayName }}{{ template.enabled ? "" : " · disabled" }}</option>
                </select></label>
              </div>
              <p>The test bypasses category routing and uses this saved SMTP profile directly. It does not change or verify settings during normal saves.</p>
              <p v-if="testEmailMessage" :class="testEmailFailed ? 'form-error' : 'settings-message'" :role="testEmailFailed ? 'alert' : 'status'">{{ testEmailMessage }}</p>
              <button class="button button--primary" type="button" :disabled="sendingTestEmail || testEmailRecipient.trim() === ''" @click="sendTestEmail">
                {{ sendingTestEmail ? "Sending…" : "Send test message" }}
              </button>
            </div>
          </form>
        </div>
      </section>

      <section v-if="activeSection === 'email'" class="content-panel settings-card">
        <div class="settings-card__heading"><div><p class="eyebrow">Message routing</p><h2>Delivery categories</h2></div></div>
        <form class="settings-form routing-form" @submit.prevent="saveRouting">
          <label v-for="category in CATEGORY_OPTIONS" :key="category.value" class="routing-field">
            <span><strong>{{ category.label }}</strong><small>{{ category.description }}</small></span>
            <select v-model="routeSelections[category.value]">
              <option value="">Not configured · delivery disabled</option>
              <option v-for="profile in settingsStore.smtpProfiles?.profiles ?? []" :key="profile.id" :value="profile.id">{{ profile.name }}{{ profile.enabled ? "" : " · disabled" }}</option>
            </select>
          </label>
          <div class="notice notice--warning">There is no implicit fallback between categories. Unconfigured categories remain fail-closed.</div>
          <p v-if="routingMessage" :class="routingSaveFailed ? 'form-error' : 'settings-message'" :role="routingSaveFailed ? 'alert' : 'status'">{{ routingMessage }}</p>
          <button class="button button--primary" type="submit" :disabled="savingRouting">{{ savingRouting ? "Saving…" : "Save message routing" }}</button>
        </form>
      </section>

      <EmailTemplateSettings v-if="activeSection === 'email'" />
    </div>
  </AppShell>
</template>
